import limiterWorkletUrl from '../../services/limiterWorklet.js?url'

export let sharedAudioContext: AudioContext | null = null

// Other AudioContexts that ALSO need the gesture-driven unlock (e.g.
// the dedicated 48kHz context RNNoise uses for its AudioWorklet —
// rnnoise.ts can't share `sharedAudioContext` because the system
// default sample rate isn't guaranteed to be 48kHz, and a mismatch
// makes the worklet emit garbage). Registered contexts get resumed
// on every gesture the global listener captures AND on every call to
// `resumeRegisteredAudioContexts`. Use a WeakSet so closed contexts
// don't leak references.
const auxAudioContexts = new Set<AudioContext>()

export function registerAuxAudioContext(ctx: AudioContext) {
  auxAudioContexts.add(ctx)
  // Try to revive immediately — if a transient user activation is
  // still in scope from the click that just triggered the registration,
  // this succeeds without waiting for the next gesture. Critical for
  // the RNNoise path: the dedicated context is created during
  // `mediaManager.setMicrophoneEnabled`, which runs after the
  // Accept-call click and several awaits later — but often still
  // within the ~5 sec activation window.
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {})
  }
}

export function unregisterAuxAudioContext(ctx: AudioContext) {
  auxAudioContexts.delete(ctx)
}

function resumeAllManagedContexts() {
  const list: AudioContext[] = []
  if (sharedAudioContext && sharedAudioContext.state === 'suspended') {
    list.push(sharedAudioContext)
  }
  for (const ctx of auxAudioContexts) {
    if (ctx.state === 'suspended') list.push(ctx)
  }
  for (const ctx of list) {
    ctx.resume().catch(() => {})
  }
  return list.length
}

/**
 * Resume every managed (shared playback + aux denoiser) AudioContext that is
 * currently suspended. Cheap + idempotent — `resume()` is a no-op on a running
 * context. Exposed so the mediaManager health-check can revive a denoiser
 * context that suspended MID-CALL while the tab is FOREGROUND: Chromium audio
 * power-saving / output-device change can suspend it with no gesture and no
 * visibilitychange to trigger the other resume paths, so the denoiser worklet
 * stops and the published mic goes SILENT (the "перестал работать микрофон" /
 * DM "не слышно" root cause that the legacy reapply storm was crudely masking).
 * Returns how many contexts it tried to resume.
 */
export function resumeManagedAudioContexts(): number {
  return resumeAllManagedContexts()
}

/**
 * Resume every suspended managed AudioContext and AWAIT the result, returning
 * `true` only if every DENOISER (aux) context ends up running — i.e. the
 * published mic's graph is actually live, not silently suspended.
 *
 * Unlike `resumeManagedAudioContexts()` (fire-and-forget, returns a count), this
 * is awaitable + verifying. The WARM-unmute fast-path (`mediaContext`) needs to
 * KNOW the denoiser graph came back BEFORE it unmutes the published track into
 * it. The per-second health tick that normally keeps the graph alive (#53/#65)
 * is throttled while the tab is BACKGROUNDED (mobile especially), so a
 * warm-muted graph can sit suspended; `track.unmute()` then publishes SILENCE
 * ("микрофон не включается" / mobile silent-publish). If this returns false the
 * caller must cold-republish (rebuild the graph) instead of unmuting into a dead
 * context.
 *
 * `resume()` still only succeeds inside a transient user activation, but the
 * warm unmute is normally driven by the mic-button tap, so a gesture is in
 * scope. The shared PLAYBACK context is resumed too (harmless, idempotent) but
 * not part of the verdict — it powers what you HEAR, not the mic you PUBLISH; a
 * raw-mic / denoiser-'off' graph has no aux context, so the verdict is trivially
 * true (nothing to verify = safe to unmute the raw track).
 */
export async function resumeAndVerifyManagedAudioContexts(): Promise<boolean> {
  const toResume: AudioContext[] = []
  if (sharedAudioContext && sharedAudioContext.state === 'suspended') toResume.push(sharedAudioContext)
  for (const ctx of auxAudioContexts) {
    if (ctx.state === 'suspended') toResume.push(ctx)
  }
  await Promise.all(toResume.map((c) => c.resume().catch(() => {})))
  // Verdict is gated on the DENOISER (aux) contexts only — those back the
  // published mic. 'closed' = already torn down (not our concern). Any aux still
  // 'suspended' after the awaited resume = couldn't revive → caller cold-rebuilds.
  for (const ctx of auxAudioContexts) {
    if (ctx.state === 'suspended') return false
  }
  return true
}

export function getSharedAudioContext(): AudioContext {
  if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext
    sharedAudioContext = new AudioContext()
    // Belt-and-braces with the global gesture listener below: try to
    // resume immediately. If the AudioContext is being created in
    // direct response to a user gesture (e.g. Accept-call → connect
    // chain), Chrome's transient activation will still be in scope
    // for ~5 sec and resume() succeeds without needing the next gesture.
    // The 5 sec window is plenty for typical setups but expires under
    // slow LiveKit handshakes; for those, the global listener kicks in
    // on the user's next click. Either way, no .catch toast — silence
    // is the worst case and `totalSamplesReceived` stays at 0 (visible
    // in webrtc-internals) so the dev can tell it didn't fire.
    sharedAudioContext.resume().catch(() => {})
  } else if (sharedAudioContext.state === 'suspended') {
    // Existing context but stuck suspended (e.g. window backgrounded
    // and OS suspended the audio system). Try to revive on every
    // access — cheap, idempotent.
    sharedAudioContext.resume().catch(() => {})
  }
  return sharedAudioContext
}

// Lookahead-limiter AudioWorklet module registration. addModule is per-context
// and must complete before `new AudioWorkletNode(ctx,'lookahead-limiter')`. We
// dedupe concurrent callers per context (many AudioPlayers mount at once) and
// record success in a WeakSet so graph-build code can check readiness
// synchronously. Re-registers automatically if the shared context is recreated.
const limiterReadyCtx = new WeakSet<BaseAudioContext>()
const limiterPromises = new WeakMap<BaseAudioContext, Promise<boolean>>()

export function ensureLimiterWorklet(ctx: BaseAudioContext): Promise<boolean> {
  if (limiterReadyCtx.has(ctx)) return Promise.resolve(true)
  const existing = limiterPromises.get(ctx)
  if (existing) return existing
  const p = (async () => {
    try {
      await ctx.audioWorklet.addModule(limiterWorkletUrl)
      limiterReadyCtx.add(ctx)
      return true
    } catch (err) {
      console.warn('[audioContext] lookahead-limiter worklet addModule failed:', err)
      limiterPromises.delete(ctx) // allow a later retry
      return false
    }
  })()
  limiterPromises.set(ctx, p)
  return p
}

/** Sync check for graph-build code: is the limiter worklet ready on this ctx? */
export function isLimiterWorkletReady(ctx: BaseAudioContext): boolean {
  return limiterReadyCtx.has(ctx)
}

/**
 * Eagerly create + resume the shared playback AudioContext while a
 * user gesture is in scope (e.g. the Login form submit). Call it from
 * a real gesture handler — see invariant #19.
 *
 * Why: a browser starts an AudioContext 'suspended' unless it's
 * created/resumed during a transient user activation. Today the shared
 * context is built lazily — only when the first `AudioPlayer` mounts on
 * call-join (LiveKitBackgroundAudio). That moment is several awaits past
 * the Join click, so the activation window has usually expired and the
 * context comes up 'suspended'. The user joins a call and hears nothing
 * on the PLAYBACK side until their next click revives it (the silent-
 * call complaint; the publish side of the same trap is invariants
 * #10/#16). Warming the context at login means the first call the user
 * joins already has 'running' playback — no first-click dead air.
 *
 * Best-effort and idempotent: re-running returns the same singleton
 * context, and if the gesture window is already gone the global gesture
 * listener + the visibilitychange net (invariant #18) still revive it
 * on the next interaction. NEVER touches the microphone — no
 * getUserMedia, no permission prompt, no denoiser graph. Pure playback-
 * context warmup, so it's safe to fire even on a login that then fails.
 */
export function prewarmVoiceAudio(): void {
  if (typeof window === 'undefined') return
  try {
    const ctx = getSharedAudioContext()
    // Preload the lookahead-limiter worklet module now (login) — it's DEFAULT ON
    // (invariant #44/#45), so having it registered before the first call means it
    // gets inserted on the very first peer's graph build, not a rebuild later.
    void ensureLimiterWorklet(ctx)
    console.info(
      `[audioContext] prewarm: shared context state='${ctx.state}' ` +
        `sampleRate=${ctx.sampleRate}.`,
    )
  } catch (err) {
    // Warmup is opportunistic — never let it break the login flow.
    console.warn('[audioContext] prewarm failed (non-fatal):', err)
  }
}

export function closeSharedAudioContext() {
  if (sharedAudioContext && sharedAudioContext.state !== 'closed') {
    sharedAudioContext.close().catch(() => {})
    sharedAudioContext = null
  }
  // Closing the shared context drops the tap with it. Reset so the
  // next call to `getPlaybackReferenceNode` rebuilds against the
  // fresh context.
  playbackReferenceNode = null
}

// Reference-audio tap on the shared AudioContext. Used by the
// DeepFilterNet AEC stage (see services/dfn/dfnWorker.ts) to subtract
// what's playing in the speakers from what the mic captured. Every
// per-peer GainNode in LiveKitBackgroundAudio.AudioPlayer connects
// here in addition to ctx.destination, so this node sees exactly the
// signal the user is hearing (after deafen + peer-volume).
//
// Module-level singleton: there's one playback mix per call, not
// per-peer. The tap stays alive for the whole shared-context lifetime
// even when DFN's AEC stage isn't reading from it — the cost of an
// unread MediaStreamAudioDestinationNode is negligible and matches
// how the rest of the audio graph is wired (we never tear down peer
// gain nodes either; React + LiveKit handle that on participant
// teardown).
let playbackReferenceNode: MediaStreamAudioDestinationNode | null = null

/**
 * Return (creating if needed) the shared playback-reference node.
 * AudioPlayer mounts call this once and `gain.connect(node)` so the
 * mix accumulates here. The DFN AEC stage reads
 * `node.stream.getAudioTracks()[0]` to feed its NLMS filter.
 */
export function getPlaybackReferenceNode(): MediaStreamAudioDestinationNode {
  const ctx = getSharedAudioContext()
  if (!playbackReferenceNode || playbackReferenceNode.context !== ctx) {
    playbackReferenceNode = ctx.createMediaStreamDestination()
  }
  return playbackReferenceNode
}

/**
 * Convenience accessor for the reference track. Returns null only
 * when the shared context hasn't yet been built (no AudioPlayer
 * mounted, no call active) — caller should defer in that case.
 */
export function getPlaybackReferenceTrack(): MediaStreamTrack | null {
  const node = getPlaybackReferenceNode()
  const track = node.stream.getAudioTracks()[0]
  return track ?? null
}

/**
 * Resolve an audio-output sink target.
 *
 * Originally added in v0.1.180A as an attempt to lock setSinkId to a
 * concrete deviceId so OS-default flips on mic switch couldn't reroute
 * Web Audio playback. Turned out to make things WORSE for Bluetooth
 * users: BT headsets expose A2DP (high-quality output) and HFP
 * (bi-directional narrow-band) as separate deviceIds; the BT stack
 * swaps between them when the picked mic forces a profile change. If
 * we lock to the A2DP deviceId at join time and the stack flips to
 * HFP later, the original deviceId disappears from the device tree
 * and Chromium falls back to silence (or default). The original
 * `'default'` behaviour, by contrast, is dynamic — Chromium re-resolves
 * it whenever the OS default playback device changes, which is
 * exactly what BT profile switching wants.
 *
 * So we go back to a simple pass-through:
 *   - explicit user pick (concrete deviceId)             → return as-is
 *   - the literal `'default'` written by the picker        → return as-is
 *   - null / undefined                                     → 'default'
 *
 * The mic-switch routing issue that motivated the original fix turned
 * out to be the same BT-profile switch reported by the same user —
 * not a separate Windows comms-default flip. Reverting here doesn't
 * regress anything that ever worked.
 */
export async function resolveAudioOutputSink(picked: string | null | undefined): Promise<string> {
  return picked || 'default'
}

// Browser autoplay policy: an AudioContext created before any user
// gesture starts in 'suspended' state. ctx.resume() ONLY succeeds
// while a transient user activation is still in scope — which is gone
// the moment we hit even one await on the call-connect chain. The
// previous best-effort `ctx.resume().catch(() => {})` inside the
// per-track AudioPlayer mount path silently failed in this exact
// situation, and remote audio packets were decoded by WebRTC but
// never made it to the speakers (chrome://webrtc-internals confirmed
// `bytesReceived` growing while the user heard silence).
//
// Install a one-shot, capture-phase listener for every user gesture
// kind Chrome counts as activation; the very first one resumes the
// context and tears down the listeners. Lazy-attached on first call
// so SSR / test environments aren't affected.
let unlockInstalled = false
function installAudioContextUnlock() {
  if (unlockInstalled) return
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  unlockInstalled = true
  const events = ['pointerdown', 'mousedown', 'keydown', 'touchstart'] as const
  const teardown = () => {
    for (const evt of events) {
      document.removeEventListener(evt, resume, true)
    }
  }
  const resume = () => {
    // Walk every managed context (shared + RNNoise + any future aux).
    // Each one might be in a different state. Tear the listeners down
    // only after EVERY context is running — otherwise a still-suspended
    // RNNoise context would never get revived after the shared one
    // unlocked first.
    const sharedReady = !sharedAudioContext || sharedAudioContext.state === 'running' || sharedAudioContext.state === 'closed'
    let allAuxReady = true
    for (const ctx of auxAudioContexts) {
      if (ctx.state !== 'running' && ctx.state !== 'closed') allAuxReady = false
    }
    if (sharedReady && allAuxReady && auxAudioContexts.size === 0) {
      // Nothing to do AND nothing to unlock — keep the listener installed
      // until at least one context shows up (e.g. AudioPlayer mounts).
      return
    }
    resumeAllManagedContexts()
    // Re-check after queueing resume(). If everything is running OR
    // closed, we can detach.
    const stillBusy = (sharedAudioContext && sharedAudioContext.state === 'suspended')
      || Array.from(auxAudioContexts).some((c) => c.state === 'suspended')
    if (!stillBusy && (sharedAudioContext || auxAudioContexts.size > 0)) {
      teardown()
    }
    // If resume() is still in flight (it's async), the next gesture
    // gets another shot. The catch in resumeAllManagedContexts already
    // ignores rejections so we don't double-handle here.
  }
  for (const evt of events) {
    document.addEventListener(evt, resume, { capture: true, passive: true })
  }
}

// Auto-install when this module first loads in a browser context.
installAudioContextUnlock()

// Tab-visibility safety net. Some browsers (notably Chromium on
// long-sleeping laptops) silently suspend AudioContexts when the tab
// has been backgrounded for several minutes. The user returns to find
// the voice call still "connected" but no audio in either direction —
// our gesture-driven unlock above only fires on click/keypress, and a
// user staring at a black screen often won't click anywhere before
// reporting "the call is dead".
//
// Listen for visibilitychange and proactively resume every managed
// AudioContext when the tab becomes visible again. Idempotent: if all
// contexts are already running, the loop is a noop.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    const resumed = resumeAllManagedContexts()
    if (resumed > 0) {
      console.info(
        `[audioContext] Resumed ${resumed} AudioContext(s) on tab-foreground.`,
      )
    }
  })
}
